import { useSetState } from 'ahooks'
import { useDeepCompareEffect } from 'ahooks'
import { omit } from 'lodash-es'
import { observer } from 'mobx-react-lite'
import React, { type ReactNode, useEffect, useMemo, useState } from 'react'

import { useEventManager } from '@/components/EventManagerProvider'
import HCell from '@/components/NewEditForm/UILibrary/HYBVant/HCell'
import HPopup from '@/components/NewEditForm/UILibrary/HYBVant/HPopup'
import { CATEGORY_INFO_MAP, SELECT_MODE } from '@/constants'
import { dealCellTitle, flattenTree, findValuesByKeys } from '@/utils'

import { organizeSearchMap, TREE_SELECT_API_MAP } from './services'
import TreeSearchView from './TreeSearchView'

import './index.less'

interface ElementProps {
  type?: string
  mode?: string
  disabled?: boolean
  arrowIcon?: boolean | ReactNode
  config?: Record<string, any>
  requestParams?: Record<string, any>
  [index: string]: any
}

interface TreeSelectProps {
  elementProps?: ElementProps
  [index: string]: any
}

// 处理 elementProps，兼容老逻辑
const dealProps = (props: any) => {
  const { elementProps, searchParams, selectorParams, ...rest } =
    findValuesByKeys(props, ['elementProps', 'searchParams', 'selectorParams']) || {}
  let tempSearchParams = searchParams || selectorParams
  // 兼容老的配置
  if (elementProps?.type) {
    const { searchParams } = elementProps?.[elementProps?.type] || {}
    tempSearchParams = searchParams
  }
  return {
    ...elementProps,
    searchParams: {
      with_no_auth: false,
      paged: false,
      enable_status: 1,
      ...tempSearchParams
    }
  }
}

function TreeSelect(props: TreeSelectProps) {
  const {
    onConfirm,
    onChange,
    elementProps,
    category = CATEGORY_INFO_MAP.Organize.category,
    ...restElementProps
  } = props
  const { config, arrowIcon, include_child = 0, searchParams = {} } = dealProps(props)

  // 组件配置
  const { defaultSelect, type: configType } = config || {}

  // 类型兼容 config.type 和 category 字段 名称为 帕斯卡写法（首字母大写）
  const fieldType = configType || category

  // 是否是组织
  const isOrg = fieldType?.toLowerCase()?.includes('org')

  const labelText = CATEGORY_INFO_MAP?.[fieldType]?.label || ''

  const mode = props.mode || findValuesByKeys(props, ['mode'])?.mode || SELECT_MODE.SINGLE
  const isSingle = mode === SELECT_MODE.SINGLE

  const [{ visible, checkedValue }, setState] = useSetState({
    visible: false,
    checkedValue: []
  })

  // 接口
  const treeApiMap = TREE_SELECT_API_MAP[fieldType] || organizeSearchMap
  // 是否需要权限 with_no_auth 为 true 则不需要权限
  const needAuth = !searchParams?.with_no_auth

  // 最终的接口
  const finalTreeApiMap = useMemo(() => {
    return {
      Query: treeApiMap.Query,
      Search: needAuth ? treeApiMap.Auth : treeApiMap.NoAuth
    }
  }, [needAuth])

  const eventManager = useEventManager()

  // 触发回调用 setSate
  const triggerCallback = (data: any) => {
    eventManager?.emit('NewEditForm.setState', {
      Organize: omit(data, ['childs', 'children'])
    })
  }

  // 处理 value
  const value: any = useMemo(() => {
    const tempValue = props?.value
    if (Array.isArray(tempValue)) {
      return tempValue?.map((x: any) => (typeof x === 'string' ? { id: x } : x))
    }
    if (tempValue && typeof tempValue === 'string') {
      return [{ id: tempValue }]
    }
    return tempValue ? [tempValue] : []
  }, [props?.value])

  // 初始化默认选中值
  const initSuccess = () => {
    const id_list = value?.map((x: any) => x?.id)
    treeApiMap.NoAuth({ id_list }).then(({ success, data }: any) => {
      if (success) {
        const node = flattenTree(data)
          ?.map((item: any) => {
            // 若为单选的组织展示三级，多选展示一级
            const name =
              isOrg && item.org_names && isSingle
                ? item.org_names?.slice(0, 3)?.join?.('/')
                : item.name
            return { id: item.id, name }
          })
          .filter((x: any) => id_list.includes(x?.id))
        setState({ checkedValue: node })
        triggerCallback(node)
      }
    })
  }

  const fetchEmployeeSelector = async () => {
    const { data } = await treeApiMap.Query(searchParams)
    return (data?.item_infos || []).find((x: any) => x?.code === 'org').item_values || []
  }

  // 初始化默认选中值
  useEffect(() => {
    if (defaultSelect && !value?.[0]?.id) {
      fetchEmployeeSelector().then((res) => {
        const defaultValue = [res?.[0]?.childs?.[0]]
        setState({
          checkedValue: defaultValue as any
        })
        onConfirm?.(defaultValue)
      })
    }
  }, [defaultSelect])

  useDeepCompareEffect(() => {
    setState({ checkedValue: value as any })
    const currentId = value?.[0]?.id
    if (currentId) {
      initSuccess()
    }
  }, [value])

  const onOk = (data: any) => {
    const params = data?.map((x: any) => omit({ ...x, include_child }, ['childs', 'children']))
    setState({
      checkedValue: params
    })
    const val = isSingle ? params?.[0] : params
    onConfirm?.(val)
    onChange?.(val)
    onClose()
    triggerCallback(val)
  }
  const onClose = () => {
    setState({ visible: false })
  }

  const cellTitle = dealCellTitle({
    value: checkedValue,
    suffix: labelText
  })

  const bool: boolean = !props?.disabled && !props?.readed

  return (
    <>
      {props?.children ? (
        <div
          className='h-tree-select-cell-wrap'
          onClick={() => !props?.disabled && setState({ visible: true })}
        >
          {props?.children}
        </div>
      ) : (
        <HCell
          value={cellTitle}
          onClick={() => bool && setState({ visible: true })}
          disabled={props?.disabled}
          placeholder={props?.placeholder}
          arrowIcon={arrowIcon}
          readed={props?.readed}
        />
      )}
      <HPopup
        className='h-tree-select-popup'
        title={props?.title || `选择${props.label || ''}`}
        closeOnMaskClick
        visible={visible}
        size='xlg'
        onMaskClick={onClose}
        onCancel={onClose}
        hasOk={false}
      >
        {visible && (
          <TreeSearchView
            searchParams={searchParams}
            treeApiMap={finalTreeApiMap}
            {...restElementProps}
            needAuth={needAuth}
            value={checkedValue}
            type={props?.type || ''}
            mode={mode}
            onClose={onClose}
            onConfirm={onOk}
          />
        )}
      </HPopup>
    </>
  )
}

/**
 * 前置化转换 - 用于回显
 * @param value
 * @param config
 * @returns newValue
 */
TreeSelect.convertValue = (value: any, config?: any, values?: any) => {
  if (values && Array.isArray(config?.names) && config?.names?.length === 2) {
    if (values?.[config?.names[0]] && values?.[config?.names[1]]) {
      return {
        [config?.names[0]]: [
          {
            id: values[config?.names[0]],
            name: values[config?.names[1]]
          }
        ]
      }
    }
  }
  return value
}
/**
 * 提交时转换
 * @param value
 * @param config
 * @returns newValue
 */
TreeSelect.transform = (value: any, config?: any) => {
  if (value && Array.isArray(config?.names) && config?.names?.length === 2) {
    return {
      [config?.names[0]]: value?.[0]?.id,
      [config?.names[1]]: value?.[0]?.name
    }
  }
  return value?.id
}
export default observer(TreeSelect)
